// eslint-disable-next-line no-restricted-imports
import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Flex,
  GridAlpha,
  Heading,
  HelpText,
  Link,
  Select,
  Tag,
  Text,
} from '@applyboard/crystal-ui';
import { CheckCircleFillIcon } from '@applyboard/ui-icons';
import { useEventTracker, useTranslation } from '@applyboard/ui-utils';
import { RbcLogo2 } from '../../assets/RbcLogo2';
import { TdLogo } from '../../assets/TdLogo';
import { FULL_COUNTRY_OPTIONS_LIST } from '../../common/constants';
import { useGetTrackingEventData } from '../../common/hooks/useQueryParams';
import { StudentDetails } from '../../components/LandingPage/StudentDetails';
import { autoFillDataToRbcFormState } from '../RbcGicFormNew/FormStateMapper';
import RbcGicForm from '../RbcGicFormNew/RbcGicFormStepper';
import { autoFillDataTDFormState } from '../TdGicForm/FormStateMapper';
import TdGicForm from '../TdGicForm/TdGicForm';
import {
  GICVendors,
  GicLandingPageEvents,
  RBC_COMPONENT_STR,
  RBC_INFO_LINK_HREF,
  TD_COMPONENT_STR,
  TD_INFO_LINK_HREF,
  allValidGicCountriesOptionList,
  validRBCGicCountriesArray,
  validTDGicCountriesArray,
} from './constants';

const ENABLED_GIC_CARD_OPACITY = 1;
const DISABLED_GIC_CARD_OPACITY = 0.25;

const determineValidGics = (
  inputCountry: string,
  validRBCGicCountriesArray: string[],
): GICVendors[] => {
  const output: GICVendors[] = [];
  if (validRBCGicCountriesArray.includes(inputCountry)) {
    output.push(GICVendors.RBC);
  }
  if (validTDGicCountriesArray.includes(inputCountry)) {
    output.push(GICVendors.TD);
  }

  return output;
};

interface GicLandingPageProps {
  studentId?: string;
  studentDetails?: StudentDetails;
  isStudentDetailsError?: boolean;
  isCurrentUserApplyingForThemselves?: boolean;
  validateValuesOnLoad?: boolean;
  isVasRbcGicExtendedCountriesEnabled?: boolean;
}

interface EventMetadata {
  rpId: string;
  utmSource: string;
  source: string;
}

interface CardProps {
  setSelectedGic: React.Dispatch<React.SetStateAction<string | undefined>>;
  eventMetadata: EventMetadata;
  disabled: boolean;
}

const TdCard = ({ setSelectedGic, eventMetadata, disabled }: CardProps) => {
  const { t } = useTranslation();
  const { trackEvent } = useEventTracker();
  const selectCallback = useCallback(() => {
    trackEvent({
      event: GicLandingPageEvents.BankSelected,
      data: {
        bank_selected: 'TD',
        rp_id: eventMetadata.rpId,
        utm_source: eventMetadata.utmSource,
        source: eventMetadata.source,
      },
    });
    setSelectedGic(TD_COMPONENT_STR);
  }, [setSelectedGic, trackEvent, eventMetadata]);

  return (
    <Card as="section" emphasis="elevated" height="fill">
      <Flex direction="column" align="center" height="fill">
        <TdLogo />
      </Flex>
      <Card.Header>
        <Heading align="center" level={2} intent="primary" variant="titleS">
          {t('landingPage.tdCard.subHeader1')}
        </Heading>
        <Heading align="center" level={3} intent="secondary" variant="headlineM">
          {t('landingPage.tdCard.header1')}
        </Heading>
      </Card.Header>
      <Box py={4}>
        <Flex direction="column" gap={2}>
          <Flex.Item>
            <Flex direction="row" gap={2} height="fill">
              <CheckCircleFillIcon contrast="mid" intent="primary" size="md" />
              <Text intent="secondary">{t('landingPage.tdCard.infoList.listItem1')}</Text>
            </Flex>
          </Flex.Item>
          <Flex.Item>
            <Flex direction="row" gap={2} height="fill">
              <CheckCircleFillIcon contrast="mid" intent="primary" size="md" />
              <Text intent="secondary">{t('landingPage.tdCard.infoList.listItem2')}</Text>
            </Flex>
          </Flex.Item>
          <Flex.Item>
            <Flex direction="row" gap={2} height="fill">
              <CheckCircleFillIcon contrast="mid" intent="primary" size="md" />
              <Text intent="secondary">{t('landingPage.tdCard.infoList.listItem3')}</Text>
            </Flex>
          </Flex.Item>
          <Flex.Item>
            <Flex direction="row" gap={2} height="fill">
              <CheckCircleFillIcon contrast="mid" intent="primary" size="md" />
              <Text intent="secondary">{t('landingPage.tdCard.infoList.listItem4')}</Text>
            </Flex>
          </Flex.Item>
        </Flex>
      </Box>
      <Card.Footer>
        <Flex direction="column" gap={3} align="center">
          <Button intent="primary" onClick={selectCallback} width="fill" disabled={disabled}>
            {t('landingPage.tdCard.button')}
          </Button>
          <Link
            href={disabled ? 'javascript:void(0)' : TD_INFO_LINK_HREF}
            mode={disabled ? undefined : 'override_user_choice_and_open_in_new_tab'}
            saliency={disabled ? 'low' : 'default'}
          >
            {t('landingPage.tdCard.infoLink')}
          </Link>
        </Flex>
      </Card.Footer>
    </Card>
  );
};

const RbcCard = ({ setSelectedGic, eventMetadata, disabled }: CardProps) => {
  const { t } = useTranslation();
  const { trackEvent } = useEventTracker();
  const selectCallback = useCallback(() => {
    trackEvent({
      event: GicLandingPageEvents.BankSelected,
      data: {
        bank_selected: 'RBC',
        rp_id: eventMetadata.rpId,
        utm_source: eventMetadata.utmSource,
        source: eventMetadata.source,
      },
    });
    setSelectedGic(RBC_COMPONENT_STR);
  }, [setSelectedGic, trackEvent, eventMetadata]);

  return (
    <Card as="section" emphasis="elevated" height="fill">
      <Flex direction="column" align="center" height="fill">
        <RbcLogo2 />
      </Flex>
      <Card.Header>
        <Heading align="center" level={2} intent="primary" variant="titleS">
          {t('landingPage.rbcCard.subHeader1')}
        </Heading>
        <Heading align="center" level={3} intent="secondary" variant="headlineM">
          {t('landingPage.rbcCard.header1')}
        </Heading>
      </Card.Header>
      <Box py={4}>
        <Flex direction="column" gap={2}>
          <Flex.Item>
            <Flex direction="row" gap={2} height="fill">
              <CheckCircleFillIcon contrast="mid" intent="primary" size="md" />
              <Text intent="secondary">{t('landingPage.rbcCard.infoList.listItem1')}</Text>
            </Flex>
          </Flex.Item>
          <Flex.Item>
            <Flex direction="row" gap={2} height="fill">
              <CheckCircleFillIcon contrast="mid" intent="primary" size="md" />
              <Text intent="secondary">{t('landingPage.rbcCard.infoList.listItem2')}</Text>
            </Flex>
          </Flex.Item>
          <Flex.Item>
            <Flex direction="row" gap={2} height="fill">
              <CheckCircleFillIcon contrast="mid" intent="primary" size="md" />
              <Text intent="secondary">{t('landingPage.rbcCard.infoList.listItem3')}</Text>
            </Flex>
          </Flex.Item>
        </Flex>
      </Box>
      <Card.Footer>
        <Flex direction="column" gap={3} align="center">
          <Button intent="primary" onClick={selectCallback} width="fill" disabled={disabled}>
            {t('landingPage.rbcCard.button')}
          </Button>
          <Link
            href={disabled ? 'javascript:void(0)' : RBC_INFO_LINK_HREF}
            mode={disabled ? undefined : 'override_user_choice_and_open_in_new_tab'}
            saliency={disabled ? 'low' : 'default'}
          >
            {t('landingPage.rbcCard.infoLink')}
          </Link>
        </Flex>
      </Card.Footer>
    </Card>
  );
};

/** This page should not be used directly but rather through `PublicGicLandingPage` or `AuthGicLandingPage` */
const GicLandingPage = ({
  studentId,
  studentDetails,
  isStudentDetailsError,
  isCurrentUserApplyingForThemselves,
  validateValuesOnLoad = false,
  isVasRbcGicExtendedCountriesEnabled = false,
}: GicLandingPageProps) => {
  const studentCountry = studentDetails?.student?.nationality ?? '';
  const studentCountryLabel = studentCountry
    ? FULL_COUNTRY_OPTIONS_LIST.find(ele => ele.value === studentCountry)?.label
    : undefined;
  const [selectedGic, setSelectedGic] = useState<string>();
  const [selectedCountry, setSelectedCountry] = useState<string>(studentCountry);
  const isCountrySelectionLocked = !!studentCountry;
  const { t } = useTranslation();
  const { rpId, utmSource, source } = useGetTrackingEventData();

  const valuesToExclude = ['VN', 'CN', 'PH'];

  const validGicVendors = determineValidGics(
    selectedCountry,
    isVasRbcGicExtendedCountriesEnabled
      ? validRBCGicCountriesArray
      : validRBCGicCountriesArray.filter(country => !valuesToExclude.includes(country)),
  );

  const { trackEvent } = useEventTracker();
  const trackingEventMetadata: EventMetadata = {
    rpId: rpId,
    utmSource: utmSource,
    source: source,
  };
  const isTDEnabled = validGicVendors.includes(GICVendors.TD);
  const tdCardOpacity = isTDEnabled ? ENABLED_GIC_CARD_OPACITY : DISABLED_GIC_CARD_OPACITY;
  const isRBCEnabled = validGicVendors.includes(GICVendors.RBC);
  const rbcCardOpacity = isRBCEnabled ? ENABLED_GIC_CARD_OPACITY : DISABLED_GIC_CARD_OPACITY;

  useEffect(
    () =>
      trackEvent({
        event: GicLandingPageEvents.PageView,
        data: {
          rp_id: trackingEventMetadata.rpId,
          utm_source: trackingEventMetadata.utmSource,
          source: trackingEventMetadata.source,
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  switch (selectedGic) {
    case TD_COMPONENT_STR:
      return (
        <TdGicForm
          studentId={studentId}
          preFillFormState={autoFillDataTDFormState(studentDetails)}
          isStudentDetailsError={isStudentDetailsError}
          isCurrentUserApplyingForThemselves={isCurrentUserApplyingForThemselves}
          validateValuesOnLoad={validateValuesOnLoad}
        />
      );
    case RBC_COMPONENT_STR:
      return (
        <RbcGicForm
          studentId={studentId}
          preFillFormState={autoFillDataToRbcFormState(studentDetails)}
          isStudentDetailsError={isStudentDetailsError}
          isCurrentUserApplyingForThemselves={isCurrentUserApplyingForThemselves}
          validateValuesOnLoad={validateValuesOnLoad}
        />
      );
  }

  return (
    <Flex grow={1}>
      <Flex.Item grow={1}>
        <div
          style={{
            backgroundImage: `url('data:image/svg+xml,<svg width="100%" height="462" viewBox="0 0 1440 462" fill="none" xmlns="http://www.w3.org/2000/svg"><g filter="url(%23filter0_b_42_13958)"><path d="M-480 0H1920V119.556L-480 419.555V0Z" fill="%23F5F8FF"/></g><path d="M1320 194.006L1786.67 135.653V206.764L1320 265.117V194.006Z" fill="%23E6EFFE"/><path d="M840 183.787L1520 98.4443V169.555L840 254.898V183.787Z" fill="%23E6EFFE"/><path d="M1017.78 90.3522L1484.44 32V103.111L1017.78 161.463V90.3522Z" fill="%23D9E7FE"/><path d="M-80 298.463L386.667 240.11V311.221L-80 369.574V298.463Z" fill="%23E6EFFE"/><path d="M-248.889 390.672L217.778 332.32V403.431L-248.889 461.783V390.672Z" fill="%23D9E7FE"/><defs><filter id="filter0_b_42_13958" x="-591.111" y="-111.111" width="2622.22" height="641.778" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feGaussianBlur in="BackgroundImageFix" stdDeviation="55.5556"/><feComposite in2="SourceAlpha" operator="in" result="effect1_backgroundBlur_42_13958"/><feBlend mode="normal" in="SourceGraphic" in2="effect1_backgroundBlur_42_13958" result="shape"/></filter></defs></svg>')`,
            backgroundRepeat: 'no-repeat',
            backgroundPositionY: '37%',
            backgroundSize: '100vw auto',
          }}
        >
          <Box pt={14} pb={28}>
            <GridAlpha
              columns={{
                xs: '1fr',
                sm: '1fr',
                md: '1fr',
                lg: '1fr',
                xl: '1fr',
              }}
              rows={{
                xs: '1fr',
                sm: '1fr',
                md: '1fr',
                lg: '1fr',
                xl: '1fr',
              }}
            >
              <GridAlpha.Item
                columnStart={{
                  xs: 1,
                  sm: 1,
                  md: 1,
                  lg: 1,
                  xl: 1,
                }}
                columnEnd={{
                  xs: 1,
                  sm: 1,
                  md: 1,
                  lg: 1,
                  xl: 1,
                }}
              >
                <Flex direction="column" gap={4} align="center" pb={6} px={16}>
                  <Tag intent="primary" onClick={function Na() {}} size="md">
                    {t('landingPage.headerChip')}
                  </Tag>
                  <Heading align="center" level={1} intent="primary" variant="headlineL">
                    {t('landingPage.mainHeader')}
                  </Heading>
                  <Heading align="center" level={3} intent="primary" variant="titleS">
                    {t('landingPage.subHeader1')}
                  </Heading>
                  <div style={{ minWidth: 200 }}>
                    <Select
                      appearance="styled"
                      emphasis="outlined"
                      intent="secondary"
                      onChange={(value: string) => {
                        setSelectedCountry(value);
                      }}
                      value={selectedCountry}
                      label=" "
                      placeholder={
                        isCountrySelectionLocked
                          ? studentCountryLabel ?? t('landingPage.unsupportedCountryPlaceholder')
                          : t('landingPage.subHeader1')
                      }
                      disabled={isCountrySelectionLocked ? true : false}
                      helpText={
                        isCountrySelectionLocked
                          ? studentCountryLabel
                            ? t('landingPage.preselectedCountry')
                            : t('landingPage.preselectedUnsupportedCountry')
                          : ''
                      }
                    >
                      {allValidGicCountriesOptionList(t).map(optObj => {
                        return (
                          <Select.Option
                            key={optObj.value}
                            label={optObj.label}
                            value={optObj.value}
                          />
                        );
                      })}
                    </Select>
                  </div>
                </Flex>
              </GridAlpha.Item>
            </GridAlpha>
            <GridAlpha
              columns={{
                xs: '1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr',
                sm: '1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr',
                md: '1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr',
                lg: '1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr',
                xl: '1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr',
              }}
              rows={{
                xs: '1fr',
                sm: '1fr',
                md: '1fr',
                lg: '1fr',
                xl: '1fr',
              }}
            >
              <GridAlpha.Item
                columnStart={{
                  xs: 3,
                  sm: 4,
                  md: 5,
                  lg: 4,
                  xl: 4,
                }}
                columnEnd={{
                  xs: 17,
                  sm: 16,
                  md: 15,
                  lg: 16,
                  xl: 16,
                }}
              >
                <Flex direction="row" justify="end" pb={6} height="fill">
                  <div style={{ opacity: tdCardOpacity, display: 'flex', maxWidth: '790px' }}>
                    <TdCard
                      setSelectedGic={setSelectedGic}
                      eventMetadata={trackingEventMetadata}
                      disabled={!isTDEnabled}
                    />
                  </div>
                </Flex>
              </GridAlpha.Item>
              <GridAlpha.Item
                columnStart={{
                  xs: 3,
                  sm: 4,
                  md: 5,
                  lg: 17,
                  xl: 17,
                }}
                columnEnd={{
                  xs: 17,
                  sm: 16,
                  md: 15,
                  lg: 29,
                  xl: 29,
                }}
              >
                <Flex direction="row" justify="start" pb={6} height="fill">
                  <div style={{ opacity: rbcCardOpacity, display: 'flex', maxWidth: '790px' }}>
                    <RbcCard
                      setSelectedGic={setSelectedGic}
                      eventMetadata={trackingEventMetadata}
                      disabled={!isRBCEnabled}
                    />
                  </div>
                </Flex>
              </GridAlpha.Item>
              <GridAlpha.Item
                columnStart={{
                  xs: 3,
                  sm: 4,
                  md: 5,
                  lg: 4,
                  xl: 4,
                }}
                columnEnd={{
                  xs: 17,
                  sm: 16,
                  md: 15,
                  lg: 16,
                  xl: 16,
                }}
                rowStart={{
                  xs: 2,
                  sm: 2,
                  md: 2,
                }}
              >
                <Flex
                  direction="row"
                  justify={{
                    xs: 'center',
                    sm: 'center',
                    md: 'center',
                    lg: 'center',
                    xl: 'end',
                  }}
                  wrap={true}
                >
                  <Box pt={1} pb={16} px={4}>
                    <HelpText
                      intent="negative"
                      showIcon={
                        !validGicVendors.includes(GICVendors.TD) && selectedCountry ? true : false
                      }
                    >
                      {!validGicVendors.includes(GICVendors.TD) && selectedCountry ? (
                        t('landingPage.errorMessages.tdNotAvailableForCountry')
                      ) : (
                        <span>&nbsp;</span>
                      )}
                    </HelpText>
                  </Box>
                </Flex>
              </GridAlpha.Item>
              <GridAlpha.Item
                columnStart={{
                  xs: 3,
                  sm: 4,
                  md: 5,
                  lg: 17,
                  xl: 17,
                }}
                columnEnd={{
                  xs: 17,
                  sm: 16,
                  md: 15,
                  lg: 29,
                  xl: 29,
                }}
                rowStart={{
                  xs: 4,
                  sm: 4,
                  md: 4,
                  lg: 2,
                  xl: 2,
                }}
              >
                <Flex
                  direction="row"
                  justify={{
                    xs: 'center',
                    sm: 'center',
                    md: 'center',
                    lg: 'center',
                    xl: 'start',
                  }}
                  wrap={true}
                >
                  <Box pt={1} pb={16} px={4}>
                    <HelpText
                      intent="negative"
                      showIcon={
                        !validGicVendors.includes(GICVendors.RBC) && selectedCountry ? true : false
                      }
                    >
                      {!validGicVendors.includes(GICVendors.RBC) && selectedCountry ? (
                        t('landingPage.errorMessages.rbcNotAvailableForCountry')
                      ) : (
                        <span>&nbsp;</span>
                      )}
                    </HelpText>
                  </Box>
                </Flex>
              </GridAlpha.Item>
            </GridAlpha>
          </Box>
        </div>
      </Flex.Item>
    </Flex>
  );
};

export default GicLandingPage;
